import { ACCESS_LEVELS } from "../types";
import { apiClient } from "./api-client";
export class DataService {
    // Check if user has access to premium features
    hasPremiumAccess(userAccessLevel) {
        return userAccessLevel >= ACCESS_LEVELS.COMPOUND;
    }
    hasEliteAccess(userAccessLevel) {
        return userAccessLevel >= ACCESS_LEVELS.PRIME;
    }
    hasEmployeeAccess(userAccessLevel) {
        return userAccessLevel >= ACCESS_LEVELS.EMPLOYEE;
    }
    // Data fetching
    async getCaproData() {
        return apiClient.fetchCachedCaproData().then(d => d);
    }
    async getPetchemData() {
        return apiClient.fetchCachedPetchemData().then(d => d);
    }
    async getChlorineData() {
        return apiClient.fetchCachedChlorineData().then(d => d);
    }
    async getDriverData() {
        return apiClient.fetchCachedDriverData().then(d => d);
    }
    async getDriverPriceChangesData() {
        return apiClient.fetchCachedDriverPriceChangesData().then(d => d || []);
    }
    async getChemOrbisData() {
        return apiClient.fetchCachedChemOrbisData().then(d => d);
    }
    async getPetMasterPricingData() {
        return apiClient.fetchCachedPetMasterPricingData().then(d => d);
    }
    async getContractsData() {
        return apiClient.fetchCachedContractsData().then(d => d);
    }
    async getRefineryRatesData() {
        return apiClient.fetchCachedRefineryRatesData().then(d => d);
    }
    async getNewsData() {
        return apiClient.fetchCachedNewsData().then(d => d);
    }
    // Snapshot data
    async getACCPEData() {
        return apiClient.fetchCachedACCPEData().then(d => d || []);
    }
    async getACCPPData() {
        return apiClient.fetchCachedACCPPData().then(d => d || []);
    }
    async getACCPSData() {
        return apiClient.fetchCachedACCPSData().then(d => d || []);
    }
    async getACCPVCData() {
        return apiClient.fetchCachedACCPVCData().then(d => d || []);
    }
    async getACCPEDataFinal() {
        return apiClient.fetchCachedACCPEDataFinal().then(d => d || []);
    }
    async getACCPPDataFinal() {
        return apiClient.fetchCachedACCPPDataFinal().then(d => d || []);
    }
    async getACCPSDataFinal() {
        return apiClient.fetchCachedACCPSDataFinal().then(d => d || []);
    }
    async getACCPVCDataFinal() {
        return apiClient.fetchCachedACCPVCDataFinal().then(d => d || []);
    }
    async smartDefaultAccType(resin, date) {
        const prelimDataMethod = `getACC${resin.toUpperCase()}Data`;
        const finalDataMethod = `getACC${resin.toUpperCase()}DataFinal`;
        const prelimData = await this[prelimDataMethod]();
        const finalData = await this[finalDataMethod]();
        const prelimHasDate = prelimData.some((d) => d.date === date);
        const finalHasDate = finalData.some((d) => d.date === date);
        if (finalHasDate) {
            return 'final';
        }
        return 'preliminary';
    }
    async getDriverChartsData() {
        return apiClient.fetchCachedDriverChartData().then(d => d);
    }
    // Pricing forecast data
    async getFairPriceData() {
        return apiClient.fetchCachedFairPriceData().then(d => d);
    }
    async getForecastData() {
        return apiClient.fetchCachedForecastData().then(d => d);
    }
    async getForecastCommentary() {
        return apiClient.fetchCachedForecastCommentary().then(d => d);
    }
    // EIA forecast data
    async getEiaForecastData() {
        return apiClient.fetchCachedEiaForecastData().then(d => d || []);
    }
    // Generic method to fetch data by filename (for driver charts)
    async getDataByFilename(filename) {
        return apiClient.readDataFromCacheByFilename(filename).then(d => d);
    }
    async getDriverPriceChanges() {
        return apiClient.readDataFromCacheByFilename('driverPriceChanges.json').then(d => (d || []));
    }
    async getPEInventoryMetrics() {
        return apiClient.fetchCachedPEInventoryMetrics().then(d => d || []);
    }
    async getPEProductionCapacity() {
        return apiClient.fetchCachedPEProductionCapacity().then(d => d || []);
    }
    async getPEMarkets() {
        return apiClient.fetchCachedPEMarkets().then(d => d || []);
    }
    async getPERates() {
        return apiClient.fetchCachedPERates().then(d => d || []);
    }
    async getPEUSITCTrade() {
        return apiClient.fetchCachedPEUSITCTrade().then(d => d || []);
    }
    // Utility functions extracted from AngularJS
    getChange(current, previous, decimal = false, isPercent = false, divisor = 1) {
        if (previous === 0)
            return 'N/A';
        const change = current - previous;
        const changePercent = (change / previous) * 100;
        if (isPercent) {
            return changePercent.toFixed(2) + '%';
        }
        const adjustedChange = change / divisor;
        return decimal ? adjustedChange.toFixed(2) : Math.round(adjustedChange).toString();
    }
    formatLabel(dateStr) {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    }
    processData(data, isContract = false) {
        if (!Array.isArray(data))
            return [];
        return data.map(row => {
            if (isContract) {
                return {
                    ...row,
                    formattedValue: this.formatContractValue(row.value),
                    change: this.getChange(row.current, row.previous, true, false, 1)
                };
            }
            return row;
        });
    }
    formatContractValue(value) {
        if (typeof value === 'number') {
            return value.toFixed(2);
        }
        return value?.toString() || 'N/A';
    }
    // Force refresh all data - busts all caches
    async forceRefreshAllData() {
        try {
            return await apiClient.refreshAllData();
        }
        catch (error) {
            throw error;
        }
    }
}
// Export singleton instance
export const dataService = new DataService();
//# sourceMappingURL=data-service.js.map